<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const shouldBeWebhookUrl = (value = '') =>
  value ? value.startsWith('http') : true;

const store = useStore();
const { t } = useI18n();

const channelName = ref('');
const webhookUrl = ref('');
const isEvolutionApiIntegration = ref(false);

watch(isEvolutionApiIntegration, value => {
  if (value) webhookUrl.value = '';
});

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const rules = {
  channelName: { required },
  webhookUrl: { shouldBeWebhookUrl },
};

const v$ = useVuelidate(rules, { channelName, webhookUrl });

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const apiChannel = await store.dispatch('inboxes/createChannel', {
      name: channelName.value?.trim(),
      channel: {
        type: 'api',
        webhook_url: webhookUrl.value,
        additional_attributes: {
          is_evolution_api_integration: isEvolutionApiIntegration.value,
        },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: apiChannel.id,
      },
    });
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.API_CHANNEL.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.API_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.API_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.webhookUrl.$error }">
          {{ $t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.LABEL') }}
          <input
            v-model="webhookUrl"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.PLACEHOLDER')
            "
            :disabled="isEvolutionApiIntegration"
            @blur="v$.webhookUrl.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.SUBTITLE') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="isEvolutionApiIntegration"
            type="checkbox"
            class="w-4 h-4"
          />
          <span>
            {{ $t('INBOX_MGMT.ADD.API_CHANNEL.EVOLUTION_API.LABEL') }}
          </span>
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.API_CHANNEL.EVOLUTION_API.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.API_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
